import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Footer } from '../../components/footers/Footer';
import { HomeHeader } from '../../components/headers/HomeHeader';
import { PageMain } from '../../components/seo/PageMain';
import { PageSeoHelmet } from '../../components/seo/PageSeoHelmet';
import { ExploreCategories } from './components/ExploreCategories';
import { FeaturedContentSplit } from './components/FeaturedContentSplit';
import { FeaturedEbook } from './components/FeaturedEbook';
import { FeaturedReferenceGuides } from './components/FeaturedReferenceGuides';
import { HomepageFaqs } from './components/HomepageFaqs';
import { KnowledgeCenterHero } from './components/KnowledgeCenterHero';
import { PopularTopics } from './components/PopularTopics';
import { getHomeSeo } from './repository';
import './knowledgeCenter.css';

gsap.registerPlugin(useGSAP, ScrollTrigger);

export function KnowledgeCenter() {
  const pageScope = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash;
    if (!hash) return undefined;

    const timer = window.setTimeout(() => {
      document.getElementById(hash.replace('#', ''))?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [location.hash, location.pathname]);

  useGSAP(
    () => {
      const heroTimeline = gsap.timeline({ defaults: { ease: 'power2.out' } });
      heroTimeline
        .fromTo(
          '.kc-hero-title',
          { y: 40, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.7 }
        )
        .fromTo(
          '.kc-hero-subtitle',
          { y: 24, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.55 },
          '-=0.35'
        )
        .fromTo(
          '.kc-hero-search',
          { y: 20, opacity: 0, scale: 0.98 },
          { y: 0, opacity: 1, scale: 1, duration: 0.5 },
          '-=0.25'
        )
        .fromTo(
          '.kc-hero-stat',
          { y: 16, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.4, stagger: 0.08 },
          '-=0.2'
        );

      ScrollTrigger.create({
        trigger: '.kc-guides',
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.fromTo(
            '.kc-guides .kc-section-title',
            { y: 20, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' }
          );
          gsap.fromTo(
            '.kc-guide-card',
            { y: 28, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.5,
              stagger: 0.08,
              ease: 'power2.out',
            }
          );
        },
      });

      ScrollTrigger.create({
        trigger: '.kc-ebook',
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.fromTo(
            '.kc-ebook .kc-section-title',
            { y: 20, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' }
          );
          gsap.fromTo(
            '.kc-ebook-card',
            { y: 28, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.55, ease: 'power2.out' }
          );
        },
      });

      ScrollTrigger.create({
        trigger: '.kc-categories',
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.fromTo(
            '.kc-categories .kc-section-title',
            { y: 20, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' }
          );
          gsap.fromTo(
            '.kc-category-card',
            { y: 28, opacity: 0, scale: 0.96 },
            {
              y: 0,
              opacity: 1,
              scale: 1,
              duration: 0.45,
              stagger: 0.05,
              ease: 'power2.out',
            }
          );
        },
      });

      ScrollTrigger.create({
        trigger: '.kc-featured',
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.fromTo(
            '.kc-featured .kc-section-title',
            { y: 20, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' }
          );
          gsap.fromTo(
            '.kc-featured-card, .kc-dictionary',
            { y: 30, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.55,
              stagger: 0.12,
              ease: 'power2.out',
            }
          );
        },
      });

      ScrollTrigger.create({
        trigger: '.kc-homepage-faqs',
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.fromTo(
            '.kc-homepage-faqs .kc-section-title',
            { y: 20, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' }
          );
          gsap.fromTo(
            '.kc-home-faq-card',
            { y: 24, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.45,
              stagger: 0.06,
              ease: 'power2.out',
            }
          );
        },
      });

      ScrollTrigger.create({
        trigger: '.kc-topics',
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.fromTo(
            '.kc-topics .kc-section-title',
            { y: 20, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' }
          );
          gsap.fromTo(
            '.kc-topic-pill',
            { y: 14, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.35,
              stagger: 0.04,
              ease: 'power2.out',
            }
          );
        },
      });
    },
    { scope: pageScope }
  );

  return (
    <>
      <PageSeoHelmet {...getHomeSeo()} />
      <div className="kc-ds w-full overflow-x-hidden bg-white">
        <HomeHeader headerColor="#0D131E" textColor="#ffffff" />
        <PageMain ref={pageScope} className="knowledge-center-page">
          <KnowledgeCenterHero />
          <FeaturedReferenceGuides />
          <ExploreCategories />
          <FeaturedEbook />
          <FeaturedContentSplit />
          <HomepageFaqs />
          <PopularTopics />
        </PageMain>
        <Footer />
      </div>
    </>
  );
}
